// CheckoutPaymentMethodForm.jsx
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useFormikContext } from "formik";
import { useUser } from "../../../../contexts/UserContext";
import PrimaryButton, { SecondaryButton, TertiaryOutlineButton } from "../../inputs/buttons/Button";
import CheckoutLoginModal from "./CheckoutLoginModal";
import { useFetchPaymentMethods } from "../../../../utils/fetchPaymentMethods";
import CheckoutProfileSelector from "../CheckoutProfileSelector";
import { BeatLoader, PuffLoader } from "react-spinners";
import { FormikHeadlessListboxPayments } from "../../inputs/form/FormikHeadlessListbox";
import { usePageSettings } from "../../../../contexts/PageSettingsContext";

const CheckoutPaymentMethodForm = () => {
	// Modal state for login flow
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [showTooltip, setShowTooltip] = useState(false);

	// Fetch translations from PageSettingsContext
	const { translations } = usePageSettings();

	// Access user context for active profile, profiles list, logout function, etc.
	const { activeProfile, userProfiles, activateProfile, logout } = useUser();

	// Access Formik context for setting fields and validating
	const { setFieldValue, validateForm } = useFormikContext();

	// Fetch payment methods based on the active profile's ID
	const { data: paymentMethods, isLoading, isError } = useFetchPaymentMethods(activeProfile?.id);

	// Define fadeInUp motion variant (used in multiple components)
	const fadeInUp = {
		initial: { opacity: 0, y: 20 },
		animate: { opacity: 1, y: 0 },
		exit: { opacity: 0, y: 20 },
	};

	// If an error occurs fetching payment methods, automatically log out the user
	useEffect(() => {
		if (isError) {
			handleLogoutWithReset();
		}
	}, [isError]);

	/**
	 * Logs out the user and resets payment-related Formik fields.
	 * A slight delay (via setTimeout) is used to allow Formik to update its state
	 * before triggering full validation, so that the OrderSummary button updates immediately.
	 */
	const handleLogoutWithReset = async () => {
		// 1) Clear payment fields
		setFieldValue("configuration.paymentMethod", { module: "", name: "" });
		setFieldValue("configuration.paymentProfile", { id: 0, authorized: false });
		// 2) Wait a microtask tick to allow state updates to propagate
		await new Promise((resolve) => setTimeout(resolve, 0));
		// 3) Trigger a full form validation so errors update immediately
		const errors = await validateForm();
		// console.log("Errors =>", errors);
		// 4) Log out the user
		logout();
	};

	// Handler to close the login modal
	const handleCloseModal = () => setIsModalOpen(false);

	// Show tooltip if a user interacts when not logged in
	const handleTooltipClick = () => {
		if (!activeProfile) {
			setShowTooltip(true);
			setTimeout(() => setShowTooltip(false), 3000);
		}
	};

	return (
		<div className="flex flex-col px-4 md:px-0">
			{/* If no active profile, render the Login Section */}
			{!activeProfile ? (
				<LoginSection
					translations={translations}
					setIsModalOpen={setIsModalOpen}
					showTooltip={showTooltip}
					handleTooltipClick={handleTooltipClick}
				/>
			) : (
				<>
					{/* Render Active User Section */}
					<ActiveUserSection
						activeProfile={activeProfile}
						userProfiles={userProfiles}
						activateProfile={activateProfile}
						translations={translations}
						handleLogoutWithReset={handleLogoutWithReset}
						fadeInUp={fadeInUp}
					/>
					{/* Render Payment Methods Section */}
					<PaymentMethodsSection
						paymentMethods={paymentMethods}
						isLoading={isLoading}
						isError={isError}
						translations={translations}
						fadeInUp={fadeInUp}
					/>
				</>
			)}

			{/* Render Login Modal if necessary */}
			{isModalOpen && <CheckoutLoginModal isOpen={isModalOpen} onClose={handleCloseModal} />}
		</div>
	);
};

export default CheckoutPaymentMethodForm;

// ---------------------- TooltipIcon Component ----------------------
const TooltipIcon = ({ translations }) => (
	<div className="absolute top-52 left-0 w-max bg-ipxon-light-blue-gray text-ipxon-white text-xs rounded px-5 py-2.5 shadow-lg border border-ipxon-medium-dark-gray">
		<span className="flex gap-2 items-center">
			<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path
					d="M7 1C3.90721 1 1 3.90721 1 7C1 10.0928 3.90721 13 7 13C10.0928 13 13 10.0928 13 7C13 3.90721 10.0928 1 7 1ZM0 7C0 3.13401 3.13401 0 7 0C10.866 0 14 3.13401 14 7C14 10.866 10.866 14 7 14C3.13401 14 0 10.866 0 7Z"
					fill="#FAFAFA"
				/>
				<path
					d="M7 5.6C7.3866 5.6 7.7 5.9134 7.7 6.3V10.5C7.7 10.8866 7.3866 11.2 7 11.2C6.6134 11.2 6.3 10.8866 6.3 10.5V6.3C6.3 5.9134 6.6134 5.6 7 5.6Z"
					fill="#FAFAFA"
				/>
				<path
					d="M8.05 3.85C8.05 4.4299 7.5799 4.9 7 4.9C6.4201 4.9 5.95 4.4299 5.95 3.85C5.95 3.2701 6.4201 2.8 7 2.8C7.5799 2.8 8.05 3.2701 8.05 3.85Z"
					fill="#FAFAFA"
				/>
			</svg>
			{translations["TooltipMessageLogIn"]}
		</span>
	</div>
);

// ---------------------- LoginSection Component ----------------------
// Inside LoginSection Component
const LoginSection = ({ translations, setIsModalOpen, showTooltip, handleTooltipClick }) => (
	<div className="flex flex-col gap-7 pt-24 px-4 md:px-0">
		{/* Login prompt */}
		<div className="flex flex-col gap-2">
			<p className="text-ipxon-white font-bold text-base">{translations["CheckoutLoginLabel"]}</p>
			<p className="text-ipxon-medium-dark-gray text-sm font-normal flex flex-col md:flex-row gap-2">
				<span>{translations["CheckoutLoginPrompt"]}</span>
				<a
					href={process.env.GATSBY_CREATE_ACCOUNT_URL}
					target="_blank"
					rel="noopener noreferrer"
					className="text-sm text-ipxon-magenta font-bold hover:underline hover:underline-offset-8 hover:cursor-pointer"
				>
					{translations["CheckoutLoginDontHaveAnAccount"]}
				</a>
			</p>
		</div>
		<TertiaryOutlineButton
			type="button"
			onClick={() => setIsModalOpen(true)}
		>
			{translations["CheckoutLoginLabel"]}
		</TertiaryOutlineButton>		
	</div>
);

// ---------------------- ActiveUserSection Component ----------------------
const ActiveUserSection = ({
	activeProfile,
	userProfiles,
	activateProfile,
	translations,
	handleLogoutWithReset,
	fadeInUp,
}) => (
	<>
		{/* Active Profile Information & Logout Button */}
		<div className="flex justify-between gap-7 pt-7 md:pt-24 pb-7 items-center">
			<motion.p
				key={`${activeProfile.name}-${activeProfile.lastname}`}
				className="text-zinc-200 flex gap-2 items-center text-base font-bold"
				variants={fadeInUp}
				initial="initial"
				animate="animate"
				exit="exit"
				transition={{ duration: 0.2, ease: "easeOut" }}
			>
				{activeProfile.name} {activeProfile.lastname}{" "}
				<span className="text-zinc-500 text-sm font-normal">UID {activeProfile.id}</span>
			</motion.p>
			<button type="button" className="text-xs font-bold text-ipxon-magenta" onClick={handleLogoutWithReset}>
				{translations["CheckoutLoginCloseSession"]}
			</button>
		</div>

		{/* Profile Selector (if multiple profiles) */}
		{userProfiles && userProfiles.length > 0 && (
			<CheckoutProfileSelector
				profileList={userProfiles}
				selectedProfile={activeProfile}
				setSelectedProfile={activateProfile}
			/>
		)}
	</>
);

// ---------------------- PaymentMethodsSection Component ----------------------
const PaymentMethodsSection = ({ paymentMethods, isLoading, isError, translations, fadeInUp }) => (
	<div className="mt-7 border-t border-ipxon-dark-gray h-[250px]">
		{isLoading && (
			<div className="flex items-center mt-4">
				<PuffLoader size={24} color="#ffffff" />
				<p className="ml-2 text-zinc-200">{translations["ConfigurePaymentLoadingPaymentMethod"]}</p>
			</div>
		)}
		{isError && <p className="text-red-500 mt-4">{translations["ConfigurePaymentErrorLoadingPaymentMethod"]}</p>}
		{!isError && paymentMethods && (
			<>
				<motion.p
					className="text-zinc-200 mt-7 flex flex-col gap-2 text-base font-bold"
					variants={fadeInUp}
					initial="initial"
					animate="animate"
					exit="exit"
				>
					{isLoading ? translations["ConfigurePaymentLoadingPaymentMethod"] : translations["ConfigurePaymentTitle"]}
					<span className="text-zinc-500 text-sm font-normal">
						{isLoading ? (
							<BeatLoader size={6} color="#ffffff" />
						) : (
							translations["ConfigurePaymentSubtitle"]
						)}
					</span>
				</motion.p>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-7">
					<FormikHeadlessListboxPayments
						label={translations["ConfigurePaymentDropdownLabel"]}
						name="configuration.paymentMethod"
						required={false}
						options={paymentMethods.map((m) => ({
							id: m.module,
							name: m.name,
						}))}
						onChange={(selectedModule, form) => {
							if (selectedModule === "") {
								form.setFieldValue("configuration.paymentMethod", null);
							} else {
								const foundMethod = paymentMethods.find((m) => m.module === selectedModule);
								form.setFieldValue("configuration.paymentMethod", foundMethod);
							}
						}}
					/>
				</div>
			</>
		)}
	</div>
);
